/**
 * Audience Manager Page
 * Story: E007-S027 Audience Manager & Targeting Optimization
 * 
 * Main page for viewing, creating, and managing audience segments
 * with performance tracking and lookalike creation.
 */

import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useAudiences,
  useDeleteAudience,
  useSyncToPlatform,
} from '../lib/audiencesApi';
import type {
  Audience,
  Platform,
  AudienceType,
  AudienceStatus,
  AudienceFilters,
} from '../types/audiences';
import {
  formatAudienceSize,
  formatROAS,
  formatCTR,
  formatRelativeDate,
  PLATFORM_INFO,
  AUDIENCE_TYPE_INFO,
  STATUS_INFO,
  AUDIENCE_SORT_OPTIONS,
} from '../types/audiences';
import { CreateAudienceModal } from '../components/modals/CreateAudienceModal';
import { AudiencePerformanceModal } from '../components/modals/AudiencePerformanceModal';
import { CreateLookalikeModal } from '../components/modals/CreateLookalikeModal';

// ============================================
// Platform Badge Component
// ============================================
const PlatformBadge: React.FC<{ platform: Platform }> = ({ platform }) => {
  const info = PLATFORM_INFO[platform];
  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    red: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    pink: 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300',
    gray: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClasses[info.color]}`}>
      {info.name}
    </span>
  );
};

// ============================================
// Status Badge Component
// ============================================
const StatusBadge: React.FC<{ status: AudienceStatus }> = ({ status }) => {
  const info = STATUS_INFO[status];
  const colorClasses: Record<string, string> = {
    green: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    red: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    gray: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClasses[info.color]}`}>
      {info.name}
    </span>
  );
};

// ============================================
// Type Badge Component
// ============================================
const TypeBadge: React.FC<{ type: AudienceType }> = ({ type }) => {
  const info = AUDIENCE_TYPE_INFO[type];
  const isLookalike = type === 'lookalike';

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        isLookalike
          ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
          : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
      }`}
      title={info.description}
    >
      {isLookalike && '↳ '}
      {info.name}
    </span>
  );
};

// ============================================
// Performance Cell Component
// ============================================
const PerformanceCell: React.FC<{ audience: Audience }> = ({ audience }) => {
  const perf = audience.performance;
  if (!perf) return <span className="text-gray-400">-</span>;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="font-medium text-green-600 dark:text-green-400">
          {formatROAS(perf.roas)}
        </span>
        <span className="text-xs text-gray-500">ROAS</span>
      </div>
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <span>{formatCTR(perf.ctr)} CTR</span>
      </div>
    </div>
  );
};

// ============================================
// Audience Row Component
// ============================================
interface AudienceRowProps {
  audience: Audience;
  onView: (audience: Audience) => void;
  onCreateLookalike: (audience: Audience) => void;
  onSync: (audience: Audience) => void;
  onDelete: (audience: Audience) => void;
}

const AudienceRow: React.FC<AudienceRowProps> = ({
  audience,
  onView,
  onCreateLookalike,
  onSync,
  onDelete,
}) => {
  const [showActions, setShowActions] = useState(false);

  return (
    <tr
      className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      onClick={() => onView(audience)}
    >
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            {audience.type === 'lookalike' && (
              <span className="text-purple-500">↳</span>
            )}
            <span className="font-medium text-gray-900 dark:text-white">
              {audience.name}
            </span>
          </div>
          {audience.description && (
            <span className="text-xs text-gray-500 truncate max-w-xs">
              {audience.description}
            </span>
          )}
          {audience.parent_audience_name && (
            <span className="text-xs text-purple-500">
              from: {audience.parent_audience_name}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <PlatformBadge platform={audience.platform} />
      </td>
      <td className="px-4 py-3">
        <TypeBadge type={audience.type} />
        {audience.similarity_percent && (
          <span className="ml-1 text-xs text-gray-500">
            ({audience.similarity_percent}%)
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <span className="font-medium">{formatAudienceSize(audience.size)}</span>
      </td>
      <td className="px-4 py-3">
        <PerformanceCell audience={audience} />
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={audience.status} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {formatRelativeDate(audience.last_used_at)}
      </td>
      <td className="px-4 py-3">
        <div
          className={`flex items-center gap-1 transition-opacity ${
            showActions ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => onView(audience)}
            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded dark:hover:bg-blue-900"
            title="View Performance"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          {audience.type === 'custom' && (
            <button
              onClick={() => onCreateLookalike(audience)}
              className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded dark:hover:bg-purple-900"
              title="Create Lookalike"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </button>
          )}
          <button
            onClick={() => onSync(audience)}
            className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded dark:hover:bg-green-900"
            title="Sync to Platform"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          <button
            onClick={() => onDelete(audience)}
            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded dark:hover:bg-red-900"
            title="Archive"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </td>
    </tr>
  );
};

// ============================================
// Main Page Component
// ============================================
const AudienceManagerPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Filter state from URL
  const filters: AudienceFilters = useMemo(() => ({
    search: searchParams.get('search') || undefined,
    platform: (searchParams.get('platform') as Platform) || undefined,
    type: (searchParams.get('type') as AudienceType) || undefined,
    status: (searchParams.get('status') as AudienceStatus) || 'active',
    sort: searchParams.get('sort') || 'created_at_desc',
    page: parseInt(searchParams.get('page') || '1'),
    limit: 25,
  }), [searchParams]);

  // Update URL params
  const updateFilters = (updates: Partial<AudienceFilters>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, String(value));
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  };

  // Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [performanceModalAudience, setPerformanceModalAudience] = useState<Audience | null>(null);
  const [lookalikeModalAudience, setLookalikeModalAudience] = useState<Audience | null>(null);
  const [syncModalAudience, setSyncModalAudience] = useState<Audience | null>(null);

  // Queries and mutations
  const { data, isLoading, error, refetch } = useAudiences(filters);
  const deleteAudience = useDeleteAudience();
  const syncToPlatform = useSyncToPlatform();

  // Event handlers
  const handleView = (audience: Audience) => {
    setPerformanceModalAudience(audience);
  };

  const handleCreateLookalike = (audience: Audience) => {
    setLookalikeModalAudience(audience);
  };

  const handleSync = (audience: Audience) => {
    setSyncModalAudience(audience);
  };

  const handleDelete = async (audience: Audience) => {
    if (confirm(`Are you sure you want to archive "${audience.name}"?`)) {
      try {
        await deleteAudience.mutateAsync(audience.id);
        refetch();
      } catch (e) {
        console.error('Failed to delete audience:', e);
      }
    }
  };

  const handleSyncConfirm = async (targetPlatform: Platform) => {
    if (!syncModalAudience) return;
    try {
      await syncToPlatform.mutateAsync({
        audienceId: syncModalAudience.id,
        targetPlatform,
      });
      setSyncModalAudience(null);
      refetch();
    } catch (e) {
      console.error('Failed to sync audience:', e);
    }
  };

  // Pagination
  const totalPages = data?.metadata.pages || 1;
  const currentPage = filters.page || 1;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Audiences
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Manage and analyze your audience segments across platforms
          </p>
        </div>
        <button
          onClick={() => setCreateModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Create Audience
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        {/* Search */}
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search audiences..."
              value={filters.search || ''}
              onChange={(e) => updateFilters({ search: e.target.value || undefined, page: 1 })}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Platform Filter */}
        <select
          value={filters.platform || ''}
          onChange={(e) => updateFilters({ platform: (e.target.value as Platform) || undefined, page: 1 })}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">All Platforms</option>
          <option value="meta">Meta</option>
          <option value="google">Google</option>
          <option value="tiktok">TikTok</option>
        </select>

        {/* Type Filter */}
        <select
          value={filters.type || ''}
          onChange={(e) => updateFilters({ type: (e.target.value as AudienceType) || undefined, page: 1 })}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="">All Types</option>
          <option value="custom">Custom</option>
          <option value="lookalike">Lookalike</option>
          <option value="uploaded_list">Uploaded List</option>
          <option value="retargeting">Retargeting</option>
        </select>

        {/* Status Filter */}
        <select
          value={filters.status || 'active'}
          onChange={(e) => updateFilters({ status: (e.target.value as AudienceStatus) || undefined, page: 1 })}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="active">Active</option>
          <option value="archived">Archived</option>
          <option value="">All Status</option>
        </select>

        {/* Sort */}
        <select
          value={filters.sort || 'created_at_desc'}
          onChange={(e) => updateFilters({ sort: e.target.value, page: 1 })}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          {AUDIENCE_SORT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          Failed to load audiences. Please try again.
        </div>
      )}

      {/* Audiences Table */}
      {data && data.audiences.length > 0 && (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Audience
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Platform
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Used
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {data.audiences.map((audience) => (
                  <AudienceRow
                    key={audience.id}
                    audience={audience}
                    onView={handleView}
                    onCreateLookalike={handleCreateLookalike}
                    onSync={handleSync}
                    onDelete={handleDelete}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 px-4">
            <span className="text-sm text-gray-500">
              Showing {((currentPage - 1) * (filters.limit || 25)) + 1} -{' '}
              {Math.min(currentPage * (filters.limit || 25), data.metadata.total)} of{' '}
              {data.metadata.total} audiences
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => updateFilters({ page: currentPage - 1 })}
                disabled={currentPage <= 1}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Previous
              </button>
              <span className="px-3 py-1">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => updateFilters({ page: currentPage + 1 })}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Empty State */}
      {data && data.audiences.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            No audiences found
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {filters.search
              ? 'Try adjusting your search or filters'
              : 'Get started by creating your first audience'}
          </p>
          {!filters.search && (
            <button
              onClick={() => setCreateModalOpen(true)}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Audience
            </button>
          )}
        </div>
      )}

      {/* Modals */}
      {createModalOpen && (
        <CreateAudienceModal
          onClose={() => setCreateModalOpen(false)}
          onSuccess={() => {
            setCreateModalOpen(false);
            refetch();
          }}
        />
      )}

      {performanceModalAudience && (
        <AudiencePerformanceModal
          audience={performanceModalAudience}
          onClose={() => setPerformanceModalAudience(null)}
        />
      )}

      {lookalikeModalAudience && (
        <CreateLookalikeModal
          seedAudience={lookalikeModalAudience}
          onClose={() => setLookalikeModalAudience(null)}
          onSuccess={() => {
            setLookalikeModalAudience(null);
            refetch();
          }}
        />
      )}

      {/* Sync Modal */}
      {syncModalAudience && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Sync to Platform
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Select a platform to sync "{syncModalAudience.name}" to:
            </p>
            <div className="flex flex-col gap-2">
              {['meta', 'google', 'tiktok']
                .filter((p) => p !== syncModalAudience.platform)
                .map((platform) => (
                  <button
                    key={platform}
                    onClick={() => handleSyncConfirm(platform as Platform)}
                    className="flex items-center gap-3 px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <PlatformBadge platform={platform as Platform} />
                    <span className="text-gray-700 dark:text-gray-300">
                      Sync to {PLATFORM_INFO[platform as Platform].name}
                    </span>
                  </button>
                ))}
            </div>
            <button
              onClick={() => setSyncModalAudience(null)}
              className="mt-4 w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudienceManagerPage;
